// Paper fields as the dictionary offers them (modules/dictionary_module/item_files),
// each paper read back as its type and weight the way the backend sends `paper`.
// Shared by the staff and portal mock catalogues so both item pages show them at
// their true size.
//
// The flat leaflet (`dict-leaflet-2pp`): 31 options over nine paper types, with a
// micron option (polypropylene) and a tinted one, whose colour is part of the type.
// The business card's board (`dict-business-card`): papers and two named stocks,
// which carry no `paper` and are offered as types of their own.

import type { PaperStock } from "../data/catalogue-types";

export interface PaperFieldOption {
  code: string;
  label: string;
  /** Absent on a named stock. */
  paper?: PaperStock;
}

type PaperOption = PaperFieldOption & { paper: PaperStock };

function stocks(
  typeCode: string,
  typeLabel: string,
  weights: readonly number[],
  unit: "gsm" | "micron" = "gsm",
): PaperOption[] {
  return weights.map((weight) => {
    const weightLabel = unit === "gsm" ? `${weight}gsm` : `${weight} micron`;
    return {
      code: `${typeCode}-${weight}${unit === "micron" ? "mic" : ""}`,
      label: `${typeLabel} ${weightLabel}`,
      paper: { typeCode, typeLabel, weight, weightLabel },
    };
  });
}

export const LEAFLET_PAPER_STOCKS: readonly PaperOption[] = [
  ...stocks(
    "uncoated",
    "Uncoated",
    [80, 100, 120, 140, 150, 160, 170, 190, 250, 270, 300, 350],
  ),
  ...stocks("silk", "Silk", [115, 130, 150, 170, 200, 250, 300, 350, 400]),
  ...stocks("matt", "Matt", [170]),
  ...stocks("gloss", "Gloss", [170, 250]),
  ...stocks("board_gc1", "White-back board (GC1)", [330]),
  ...stocks("board_two_sided", "Two-sided board", [440]),
  ...stocks("polypropylene", "Polypropylene", [470], "micron"),
  ...stocks("synaps", "Synaps synthetic", [135, 230, 450]),
  ...stocks(
    "uncoated_tinted_pastel_green",
    "Tinted uncoated pastel green",
    [120],
  ),
];

export const LEAFLET_DEFAULT_PAPER = "silk-150";

export const BUSINESS_CARD_BOARDS: readonly PaperFieldOption[] = [
  ...stocks("uncoated", "Uncoated", [200, 340, 350, 400]),
  ...stocks("silk", "Silk", [350, 400]),
  ...stocks("uncoated_recycled", "Recycled uncoated", [350]),
  {
    code: "arena-smooth-350",
    label: "Arena Smooth Extra White 350gsm (digital)",
  },
  {
    code: "rothschild-blanks",
    label: "Rothschild pre-printed foiled blanks (overprint)",
  },
];

export const BUSINESS_CARD_DEFAULT_BOARD = "uncoated-350";
